import { usePagination } from "@/hooks/usePagination.hook";
import {
  Pagination as CustomPagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo } from "react";

type PaginationTypes = {
  total: number;
  value: number;
  onChange: (prop: number) => void;
  siblings?: number;
  boundaries?: number;
  recordPerPage: number;
};

export const Pagination = ({
  total,
  siblings = 0,
  boundaries = 1,
  value,
  onChange,
  recordPerPage,
}: PaginationTypes) => {
  const totalPages = useMemo(() => {
    if (recordPerPage <= 0) {
      throw new Error("Items per page must be greater than zero.");
    }
    return Math.ceil(total / recordPerPage);
  }, [total, recordPerPage]);

  const pagination = usePagination({
    total: totalPages,
    page: value,
    onChange: onChange,
    siblings: siblings,
    boundaries: boundaries,
  });

  const changePage = (prop: number) => {
    pagination.setPage(prop);
  };

  return (
    <CustomPagination className="justify-start">
      <PaginationContent>
        <PaginationItem
          className={cn({ "opacity-50 cursor-auto": value === 1 })}
        >
          <PaginationLink onClick={() => pagination.previous()}>
            <ChevronLeft />
          </PaginationLink>
        </PaginationItem>
        {pagination.range.map((item, index) => {
          return (
            <PaginationItem key={index}>
              {item === "dots" ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  onClick={() => changePage(item)}
                  key={index}
                  isActive={item === pagination.active}
                >
                  {item}
                </PaginationLink>
              )}
            </PaginationItem>
          );
        })}
        <PaginationItem
          className={cn({ "opacity-50 cursor-auto": value === totalPages })}
        >
          <PaginationLink onClick={() => pagination.next()}>
            <ChevronRight />
          </PaginationLink>
        </PaginationItem>
      </PaginationContent>
    </CustomPagination>
  );
};
